import { DeviceProjectAssignment } from "@/data/models/DeviceProjectAssignment";
import { Room } from "@/data/models/ProjectDetails";
import { ProjectManagementSettings } from "@/data/models/Settings";

/** The project overview pseudo-room written by IRES Project (always `roomNumber` 0). */
const OVERVIEW_ROOM_ID = "OVERVIEW";

/** Placeholder the app uses for its "add room" entry; never a real room. */
const ADD_NEW_ROOM_ID = "ADD_NEW_ROOM";

/**
 * Whether devices are assigned to rooms instead of apartments (K7).
 *
 * The client setting `deviceAssignmentSettings.roomLevelDeviceAssignment` is the only gate (absent =
 * off): there is deliberately no project-type or app-version condition (decided with IRES Project,
 * 2026-09-25), so a portal-only project or one created in IRES Office enters room mode as soon as it
 * has rooms.
 * Which apartments can still be assigned to directly is {@link canAssignDeviceToApartment}.
 *
 * Pass the live settings from `ProjectManagementSettingsModule`, so switching the setting takes
 * effect immediately. Switching it is lossless: assignments keep both `contactPersonId` and `roomId`.
 */
export function usesRoomLevelDeviceAssignment(
  projectManagementSettings: ProjectManagementSettings | null | undefined
) {
  return projectManagementSettings?.deviceAssignmentSettings?.roomLevelDeviceAssignment === true;
}

/**
 * Whether a device can be assigned to the apartment itself rather than to one of its rooms.
 *
 * - Outside room mode, and for Strombezug apartments, always.
 * - In room mode, never while the apartment has rooms: the device goes to a room.
 * - In room mode with no rooms yet, only on a portal-only project (Geräte-Zuordnung, Geräteverleih).
 *   There IRES Work is the only app that can add rooms, so blocking would leave the project without
 *   any way to assign devices. On any other project the rooms are created first, as in IRES Project.
 */
export function canAssignDeviceToApartment(
  isRoomLevelMode: boolean,
  isElectricityBillingApartment: boolean,
  assignableRoomCount: number,
  isPortalOnlyProject: boolean
) {
  if (!isRoomLevelMode || isElectricityBillingApartment) {
    return true;
  }

  return assignableRoomCount === 0 && isPortalOnlyProject;
}

/**
 * The rooms of an apartment that a device can be assigned to, ordered by `roomNumber`.
 *
 * Mirrors `RoomEntity.isRoomOfApartment` in IRES Project: the project overview room, the apartment
 * overview room (whose id is the apartment's own id) and the app's "add room" placeholder are not
 * rooms. With a single apartment every real room belongs to it, because apps other than IRES
 * Project may write a different apartment id format (same rule as the assignment list).
 *
 * @param rooms - `damagePatternData.rooms` of the project.
 * @param apartmentId - Id of the apartment (contact person).
 * @param isSingleApartment - Whether the project has exactly one real apartment.
 */
export function getAssignableRoomsOfApartment(
  rooms: Room[],
  apartmentId: string,
  isSingleApartment: boolean
): Room[] {
  return rooms
    .filter((room) =>
      room.id !== OVERVIEW_ROOM_ID &&
      room.roomNumber !== 0 &&
      room.id !== ADD_NEW_ROOM_ID &&
      room.id !== room.contactPersonId &&
      (isSingleApartment || room.contactPersonId === apartmentId)
    )
    .sort((left, right) => left.roomNumber - right.roomNumber);
}

/** One block of the devices list: the devices of a room, or the room-less devices (`room` null). */
export interface RoomAssignmentSection<T extends DeviceProjectAssignment> {
  room: Room | null;
  assignments: T[];
}

/**
 * Splits an apartment's already ordered assignments into the blocks the devices tab renders.
 *
 * The room-less block comes first: assignments with no `roomId` (legacy data, ERP-created ones,
 * devices assigned before the setting was switched on) and those whose room no longer exists,
 * because deleting a room is allowed while devices are assigned to it. Then one block per room in
 * the given order, **empty rooms included**, since in IRES Office the room header carries the
 * button that assigns to it (IRES Project uses room tabs instead and shows no empty headers).
 *
 * The incoming order is kept inside every block, so a dependent assignment stays behind its parent
 * whenever both are in the same room.
 *
 * @param assignments - The apartment's assignments in display order.
 * @param rooms - The apartment's assignable rooms, see {@link getAssignableRoomsOfApartment}.
 */
export function groupAssignmentsByRoom<T extends DeviceProjectAssignment>(
  assignments: T[],
  rooms: Room[]
): RoomAssignmentSection<T>[] {
  const roomIds = new Set(rooms.map((room) => room.id));
  const hasKnownRoom = (assignment: T) => !!assignment.roomId && roomIds.has(assignment.roomId);

  return [
    { room: null, assignments: assignments.filter((assignment) => !hasKnownRoom(assignment)) },
    ...rooms.map((room) => ({
      room,
      assignments: assignments.filter((assignment) => assignment.roomId === room.id),
    })),
  ];
}
